import { useCallback, useEffect, useRef, useState } from 'react'

export type UseSSEOptions = {
  timeoutMs?: number
  withCredentials?: boolean
  onOpen?: (es: EventSource) => void
  onMessage?: (data: string, event: MessageEvent<string>) => void
  onError?: (error: Event | Error) => void
  events?: Record<string, (event: MessageEvent<string>) => void>
}

/**
 * Lightweight React hook for managing an EventSource connection with TypeScript support.
 *
 * Example:
 * ```
 * const { connect, disconnect, isConnected } = useSSE(`${API}/sse?channels=${id}&public=1`, {
 *   events: {
 *     payment_succeeded: (event) => { ... },
 *     payment_failed: (event) => { ... },
 *   },
 * })
 * ```
 */
export function useSSE(url: string, options: UseSSEOptions = {}) {
  const { timeoutMs = 10000, withCredentials, onOpen, onMessage, onError, events } = options

  const eventSourceRef = useRef<EventSource | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  const disconnect = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (eventSourceRef.current) {
      try {
        eventSourceRef.current.close()
      } finally {
        eventSourceRef.current = null
        setIsConnected(false)
      }
    }
  }, [])

  const connect = useCallback(() => {
    return new Promise<EventSource>((resolve, reject) => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      if (!url) {
        reject(new Error('useSSE: URL is not defined'))
        return
      }

      const es = new EventSource(url, { withCredentials })

      if (timeoutMs > 0) {
        timeoutRef.current = window.setTimeout(() => {
          es.close()
          eventSourceRef.current = null
          setIsConnected(false)
          reject(new Error('SSE connection timeout'))
        }, timeoutMs)
      }

      es.onopen = () => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        setIsConnected(true)
        onOpen?.(es)
        resolve(es)
      }

      es.onmessage = (event: MessageEvent<string>) => {
        onMessage?.(event.data, event)
      }

      if (events) {
        for (const [type, handler] of Object.entries(events)) {
          es.addEventListener(type, handler as EventListener)
        }
      }

      es.onerror = (event: Event) => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        setIsConnected(false)
        onError?.(event)
        // Non-200 responses close the source for good, so no `onopen` (or retry) will follow
        if (es.readyState === EventSource.CLOSED) {
          eventSourceRef.current = null
          reject(new Error('SSE connection failed'))
        }
        // NOTE: do not reject here if already resolved to avoid unhandled states
      }

      eventSourceRef.current = es
    })
  }, [onMessage, onOpen, onError, timeoutMs, url, withCredentials, events])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [url])

  return {
    connect,
    disconnect,
    eventSource: eventSourceRef.current,
    isConnected,
  }
}
